/** The sign-in pages' frame (the brand on paper beside the form) and what their forms share. */
import type { ComponentChildren } from "preact";
import { useLayoutEffect } from "preact/hooks";
import type { ApiError } from "../../api/client.ts";
import { endonym, LANGUAGES, locale, negotiate, SLOT, t } from "../../i18n/index.ts";
import { location } from "../../lib/router.ts";
import { chooseLanguage } from "../../lib/state.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Select } from "../../ui/form.tsx";
import { SignOutIcon } from "../../ui/icons.tsx";
import { Logo } from "../../ui/Logo.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { LINK } from "../../ui/styles.ts";
import { fieldOf, returnTo, signInElsewhere } from "./flow.ts";

/** Language names in their own language ("Português (Brasil)"). */

/** Errors that mean signing in continues on the identity provider's pages (single sign-on, two-factor…). */
const ELSEWHERE = new Set(["sso_required", "extra_step_required"]);

/** The language picker: the app in another language at once (and kept for after signing in). */
function LanguagePicker() {
  return (
    <div class="w-40">
      <Select
        compact={true}
        aria-label={t("auth.language")}
        value={negotiate([locale.value])}
        onValue={chooseLanguage}
        options={LANGUAGES.map((l) => ({ value: l, label: endonym(l) }))}
      />
    </div>
  );
}

/** A tick in the logo's two pencils (vermilion, then Prussian blue over it): checked, twice. */
function Tick() {
  return (
    <svg aria-hidden="true" viewBox="0 0 100 100" class="pointer-events-none absolute -right-16 -bottom-20 size-96">
      <g fill="none" stroke-linecap="round" stroke-width="7">
        <path d="M18 58 L40 80" stroke="var(--color-vermilion)" />
        <path d="M40 80 L90 12" stroke="var(--color-prussian)" class="[mix-blend-mode:var(--pencil-blend,multiply)]" />
      </g>
    </svg>
  );
}

/** Who is signed in, on a page for someone already signed in (the terms): their email, and signing out. */
export type AuthAccount = { email: string; onSignOut: () => void };

/** A sign-in page's title, its lede, and who is signed in (when someone is); smaller on a phone for a `reader`. */
function Heading(p: { title: string; lede: ComponentChildren; account: AuthAccount | undefined; reader: boolean }) {
  return (
    <>
      <h1 class={`font-serif leading-tight text-ink ${p.reader ? "text-3xl sm:text-4xl" : "text-4xl"}`}>{p.title}</h1>
      {p.lede ? <p class={`mt-2 text-zinc-600 ${p.reader ? "text-sm sm:text-base" : ""}`}>{p.lede}</p> : null}
      {p.account ? (
        <p class="mt-1 truncate text-sm text-zinc-500">
          <Slotted text={t("auth.signedInAs", { email: SLOT })}>
            <span class="font-medium text-zinc-800">{p.account.email}</span>
          </Slotted>
        </p>
      ) : null}
    </>
  );
}

/**
 * A sign-in page: on wide screens the brand on paper to one side, the form on a sheet to the other; on narrow ones the
 * wordmark over the form. A link's `lang` (the site's sign-up buttons send it) sets the language first. For someone
 * signed in already (`account`), signing out sits beside the language picker and their email under the title. A
 * `reader` page (the terms) is the window's height, its content flexing to what's left (to scroll inside, its actions
 * kept in view).
 */
export function AuthFrame(p: {
  title: string;
  lede?: ComponentChildren;
  footer?: ComponentChildren;
  account?: AuthAccount;
  /** A wider column, the window's height, for a page with something to read (the terms). */
  reader?: boolean;
  children: ComponentChildren;
}) {
  useLayoutEffect(() => {
    const lang = location.value.searchParams.get("lang");
    if (lang && LANGUAGES.includes(lang)) {
      chooseLanguage(lang);
    }
  }, []);
  const reader = p.reader ?? false;
  return (
    <div class="min-h-dvh bg-sheet md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      <aside
        class={`relative hidden flex-col overflow-hidden border-r border-rule bg-paper p-12 md:flex ${reader ? "md:h-dvh" : ""}`}
      >
        <Logo class="text-4xl text-graphite" />
        <div class="my-auto max-w-md">
          <p class="font-serif text-5xl leading-[1.06] tracking-[-0.01em] text-pretty text-ink lg:text-6xl">
            {t("auth.pitch")}
          </p>
          <p class="mt-5 text-lg text-zinc-600">{t("login.tagline")}</p>
        </div>
        <Tick />
      </aside>
      <div
        class={
          reader
            ? "flex h-dvh flex-col overflow-y-auto px-4 pt-3 sm:px-8 sm:pt-6"
            : "flex min-h-dvh flex-col px-4 py-4 sm:px-8 sm:py-6"
        }
      >
        <header class="flex items-center justify-between gap-3">
          <Logo class={`text-graphite md:invisible ${reader ? "text-2xl sm:text-3xl" : "text-3xl"}`} />
          <div class="flex items-center gap-1 sm:gap-2">
            <LanguagePicker />
            {p.account ? (
              <Button variant="ghost" size="sm" class="-mr-1.5 text-zinc-600" onClick={p.account.onSignOut}>
                <SignOutIcon />
                {t("nav.signOut")}
              </Button>
            ) : null}
          </div>
        </header>
        {reader ? (
          <div class="flex min-h-0 flex-1 flex-col justify-center">
            <main class="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col pt-5 sm:pt-8 md:max-h-[60rem]">
              <Heading title={p.title} lede={p.lede} account={p.account} reader={true} />
              <div class="mt-4 flex min-h-0 flex-1 flex-col sm:mt-6">{p.children}</div>
            </main>
          </div>
        ) : (
          <main class="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
            <Heading title={p.title} lede={p.lede} account={p.account} reader={false} />
            <div class="mt-8">{p.children}</div>
            {p.footer ? <div class="mt-8 border-t border-rule pt-6 text-sm text-zinc-600">{p.footer}</div> : null}
          </main>
        )}
      </div>
    </div>
  );
}

/** A link to another sign-in page, keeping where to go after. */
export function AuthLink({ href, children }: { href: string; children: ComponentChildren }) {
  return (
    <Link href={href} class={LINK}>
      {children}
    </Link>
  );
}

/**
 * What went wrong with a form, unless a field shows it. When signing in continues on the identity provider's pages, a
 * button there (`email` filled in).
 */
export function FormError({ error, email = "" }: { error: ApiError | null; email?: string }) {
  if (!error || fieldOf(error)) {
    return null;
  }
  return (
    <div role="alert" class="mb-4 flex flex-col gap-3 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
      <span>{error.message}</span>
      {ELSEWHERE.has(error.code) ? (
        <Button class="self-start" onClick={() => signInElsewhere(returnTo(), email)}>
          {t("auth.continue")}
        </Button>
      ) : null}
    </div>
  );
}
